import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Mail, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authApi, userApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/components/ui/toast';

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // 图形验证码
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [needCaptcha, setNeedCaptcha] = useState(false);

  // 邮箱验证弹窗
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingUserId, setPendingUserId] = useState<number | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [dailySent, setDailySent] = useState(0);
  const [dailyLimit] = useState(3);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const { addToast } = useToast();

  const startResendCountdown = () => {
    setResendCountdown(60);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setResendCountdown((n) => {
        if (n <= 1) { clearInterval(countdownRef.current!); return 0; }
        return n - 1;
      });
    }, 1000);
  };

  const closeVerifyModal = () => {
    setShowVerifyModal(false);
    setShowOtpInput(false);
    setOtpCode('');
  };

  const fetchCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const res = await authApi.getCaptcha();
      setCaptchaToken(res.data.data.captchaToken);
      setCaptchaImage(res.data.data.captchaImage);
      setCaptchaCode('');
    } catch {
      // ignore
    } finally {
      setCaptchaLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      addToast({ title: '请输入邮箱和密码', variant: 'warning' });
      return;
    }
    setIsLoading(true);
    try {
      const extra: Record<string, string> = {};
      if (needCaptcha && captchaToken && captchaCode) {
        extra.captchaToken = captchaToken;
        extra.captchaCode = captchaCode;
      }
      const response = await authApi.login(username, password, extra);
      const { access } = response.data;
      useAuthStore.setState({ token: access });
      const userResponse = await userApi.getProfile();
      const user = userResponse.data.data?.user || userResponse.data.data || userResponse.data;
      setAuth(user, access);
      setNeedCaptcha(false);
      addToast({ title: '登录成功', description: '欢迎回来！', variant: 'success' });
      navigate('/dashboard');
    } catch (error: any) {
      const data = error.response?.data;
      const code = data?.code;

      if (code === 'EMAIL_NOT_VERIFIED') {
        setPendingEmail(data.email || '');
        setPendingUserId(data.userId ?? null);
        setShowVerifyModal(true);
        return;
      }

      if (code === 'CAPTCHA_REQUIRED') {
        setNeedCaptcha(true);
        fetchCaptcha();
        addToast({ title: '请输入图形验证码', description: '密码错误次数过多，需要输入验证码', variant: 'warning' });
        return;
      }

      if (code === 'INVALID_CAPTCHA') {
        fetchCaptcha();
        addToast({ title: '验证码错误', description: '请重新输入图形验证码', variant: 'error' });
        return;
      }

      if (code === 'ACCOUNT_LOCKED') {
        setNeedCaptcha(true);
        fetchCaptcha();
        addToast({ title: '验证码已发送', description: '请输入图形验证码后重试', variant: 'warning' });
        return;
      }

      const message = data?.detail || data?.message || '登录失败，请检查邮箱和密码';
      addToast({ title: '登录失败', description: message, variant: 'error' });

      // 登录失败后刷新验证码（如果有的话）
      if (needCaptcha) fetchCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  // ---- modal handlers ----

  const handleResend = async () => {
    if (!pendingUserId || resendCountdown > 0) return;
    try {
      const res = await authApi.resendVerification(pendingUserId);
      startResendCountdown();
      setDailySent(res.data.dailySent ?? dailySent + 1);
      addToast({ title: '验证邮件已重发', description: '请查收邮箱', variant: 'success' });
    } catch (error: any) {
      const data = error.response?.data;
      const code = data?.code;
      if (code === 'EMAIL_DAILY_LIMIT_REACHED') {
        setDailySent(data.dailySent ?? dailyLimit);
        addToast({ title: '今日已达上限', description: data.message || `每日最多发送 ${dailyLimit} 封验证邮件`, variant: 'error' });
        return;
      }
      if (code === 'OTP_RATE_LIMITED') {
        addToast({ title: '请稍后再试', description: data.message || '请等待 10 分钟后再试', variant: 'warning' });
        return;
      }
      addToast({ title: '发送失败', description: data?.message || '请稍后重试', variant: 'error' });
    }
  };

  const handleVerifyCode = async () => {
    if (!otpCode || !pendingUserId) return;
    setOtpLoading(true);
    try {
      const res = await authApi.verifyCode({ purpose: 'verify_email', userId: pendingUserId, code: otpCode });
      if (res.data.data) {
        const { user, access } = res.data.data;
        setAuth(user as any, access);
        addToast({ title: '邮箱验证成功', description: '欢迎加入 FlightSync Pro！', variant: 'success' });
        navigate('/fsop');
      }
    } catch (error: any) {
      const msg = error.response?.data?.message || '验证码错误或已过期';
      addToast({ title: '验证失败', description: msg, variant: 'error' });
    } finally {
      setOtpLoading(false);
    }
  };

  const remainingToday = Math.max(0, dailyLimit - dailySent);
  const canResend = remainingToday > 0 && resendCountdown === 0;

  // ---- Render ----

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">登录账号</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="username">邮箱</Label>
          <Input
            id="username"
            type="email"
            placeholder="请输入邮箱"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">密码</Label>
            <Link to="/forgot-password" className="text-xs text-blue-600 hover:text-blue-700">
              忘记密码？
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="请输入密码"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* ---- 图形验证码 ---- */}
        {needCaptcha && (
          <div className="rounded-lg bg-gray-50 p-3 space-y-2">
            <Label htmlFor="captchaCode" className="text-sm">图形验证码</Label>
            <div className="flex items-center gap-3">
              {captchaImage ? (
                <img
                  src={captchaImage}
                  alt="验证码"
                  className="h-10 rounded border cursor-pointer flex-shrink-0"
                  onClick={fetchCaptcha}
                  title="点击刷新"
                />
              ) : (
                <div className="h-10 w-24 bg-gray-200 rounded animate-pulse flex-shrink-0" />
              )}
              <button
                type="button"
                onClick={fetchCaptcha}
                disabled={captchaLoading}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                title="刷新验证码"
              >
                <RefreshCw className={`w-4 h-4 ${captchaLoading ? 'animate-spin' : ''}`} />
              </button>
              <Input
                id="captchaCode"
                type="text"
                maxLength={4}
                placeholder="输入验证码"
                value={captchaCode}
                onChange={(e) => setCaptchaCode(e.target.value.toUpperCase())}
                className="tracking-widest text-center text-lg"
                disabled={isLoading}
              />
            </div>
          </div>
        )}

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              登录中...
            </>
          ) : (
            '登录'
          )}
        </Button>
      </form>

      <p className="text-center text-sm text-gray-600 mt-6">
        还没有账号？{' '}
        <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium">
          立即注册
        </Link>
      </p>

      {/* ---- 邮箱验证弹窗 ---- */}
      {showVerifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 space-y-5 relative">
            <button
              type="button"
              onClick={closeVerifyModal}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">邮箱尚未验证</h2>
                <p className="text-sm text-gray-500">
                  验证邮件已发送至 <span className="font-medium">{pendingEmail}</span>
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <p className="text-sm text-gray-600">没有收到邮件？点击下方按钮重新发送。</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">今日剩余 {remainingToday}/{dailyLimit} 次</span>
              </div>
              <Button variant="outline" className="w-full" onClick={handleResend} disabled={!canResend}>
                {resendCountdown > 0
                  ? `${resendCountdown}s 后可重发`
                  : remainingToday === 0
                    ? '今日发送次数已用完'
                    : '重新发送验证邮件'}
              </Button>
            </div>

            {!showOtpInput ? (
              <button type="button" onClick={() => setShowOtpInput(true)} className="w-full text-sm text-blue-600 hover:text-blue-700">
                我已有验证码，立即验证
              </button>
            ) : (
              <div className="space-y-3">
                <Label htmlFor="otpCode" className="text-sm">输入邮件中的 6 位验证码</Label>
                <Input
                  id="otpCode"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  className="tracking-widest text-center text-lg"
                  disabled={otpLoading}
                />
                <Button className="w-full" onClick={handleVerifyCode} disabled={otpLoading || otpCode.length !== 6}>
                  {otpLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  验证并登录
                </Button>
              </div>
            )}

            <p className="text-xs text-gray-400 text-center">
              也可以直接点击邮件中的验证链接完成验证
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
